import { createTheme } from '@mui/material/styles';
import {
  borderRadius, boxShadows, breakpoints, getFontProps, palette, spaces, tooltipArrow, typography
} from './tokens';
import { colors } from './variables';
import { zIndex } from '../../../constants';

// Material UI theme documentation: https://mui.com/material-ui/customization/default-theme/
// Note that some properties do not work in theme component, but they do in Material-UI components.
// For example, the `paddingY` property is an invalid theme property, but it works in the `MuiButton` component.

const mainTheme = createTheme({
  palette,
  typography,
  breakpoints: {
    values: {
      // these are the default values from the MUI docs
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      // sideNavbar breakpoint
      collapsedSideNavbar: breakpoints.sidebarBreakpoint,
      // these are the custom values. we add one to each to solve the
      // exclusivity/inclusivity problem of breakpoint functions
      mobile: breakpoints.mobile + 1,
      tablet: breakpoints.tablet + 1,
      desktop: breakpoints.desktop + 1,
    }
  },
  components: {
    MuiDialog: {
      styleOverrides: {
        root: {
          '& .MuiPaper-root': {
            borderRadius: borderRadius.md,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
        contained: {
          boxShadow: boxShadows.elevation4,
          ':hover': {
            boxShadow: boxShadows.elevation8,
          }
        },
        outlinedPrimary: {
          ':hover': {
            borderColor: palette.primary.outlinedBorder,
          }
        },
        sizeSmall: {
          borderRadius: borderRadius.xs,
          ...(typography.button.small),
          paddingLeft: spaces.xs,
          paddingRight: spaces.xs,
          paddingTop: spaces.xxs,
          paddingBottom: spaces.xxs,
        },
        sizeMedium: {
          borderRadius: borderRadius.xs,
        },
        sizeLarge: {
          borderRadius: borderRadius.sm,
          fontSize: '1rem',
          fontWeight: 400,
          lineHeight: '1.625rem',
          letterSpacing: '0.46px',
          padding: `${spaces.xs} ${spaces.sm}`
        },
        containedInherit: {
          color: palette.text.primary,
          background: palette.secondary.contrastText,
          ':hover': {
            background: colors.neutral50,
          }
        },
        outlinedInherit: {
          borderColor: colors.neutral300,
          color: palette.text.secondary,
          ':hover': {
            background: colors.neutral200,
          },
        }
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.xs,
        },
        sizeLarge: {
          padding: spaces.sm + '!important',
        },
        sizeMedium: {
          padding: spaces.xs,
        },
        sizeSmall: {
          padding: spaces.xxs,
        },
      },
      variants: [
        {
          props: { color: 'outline' },
          style: {
            borderRadius: borderRadius.md,
            border: `1px solid ${palette.elevation.outlined}`,
            backgroundColor: palette.background.paperElevation2,
            color: palette.secondary.main,
            transition: 'box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out',
            '& .MuiTouchRipple-root': {
              display: 'none',
            },
            '&:hover': {
              backgroundColor: palette.background.paperElevation2,
              boxShadow: boxShadows.elevation3,
            },
            '&:focus': {
              backgroundColor: palette.background.paperElevation2,
              borderColor: palette.primary.outlinedBorder,
            },
            '&:disabled': {
              backgroundColor: palette.action.hover,
              color: palette.action.disabled,
            }
          },
        },
        {
          props: { color: 'outline', size: 'small' },
          style: {
            borderRadius: borderRadius.sm,
          },
        },
      ],
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          ...getFontProps('Poppins, sans-serif', 400, '1rem'),
          letterSpacing: '0.15px',
          lineHeight: '1.75rem',
          minHeight: spaces.xxl,
          gap: spaces.md,
          alignItems: 'center',
          backgroundColor: 'transparent!important',
          ':hover': {
            borderRadius: spaces.sm,
            backgroundColor: `${colors.gray100}!important`,
          },
        },
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: {
          zIndex: 1401 + ' !important',
        },
      },
    },
    MuiChip: {
      variants: [
        {
          props: { size: 'xsmall' },
          style: {
            padding: `${spaces.xxxs} 0 !important`,
            height: 20,
            borderRadius: borderRadius.xs,
            '& .MuiChip-label': {
              paddingLeft: spaces.xs,
              paddingRight: spaces.xs,
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          padding: spaces.xxs,
        },
        label: {
          // in the designs, it is 500. but this looks more like the design
          ...getFontProps('Poppins, sans-serif', 400, '0.875rem'),
          lineHeight: '1.125rem',
          letterSpacing: '0.16px',
        },
        labelMedium: {
          paddingTop: spaces.xxs,
          paddingBottom: spaces.xxs,
          paddingLeft: spaces.sm,
          paddingRight: spaces.sm,
        },
        labelSmall: {
          paddingTop: spaces.xxxs,
          paddingBottom: spaces.xxxs,
          paddingLeft: spaces.xs,
          paddingRight: spaces.xs,
        },
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          ...getFontProps('Poppins, sans-serif', 500, '0.625rem'),
          lineHeight: '0.714rem',
          borderRadius: spaces.xxs,
          backgroundColor: palette.components.tooltip.fill,
        },
        arrow: {
          width: tooltipArrow.width,
          height: tooltipArrow.height,
          color: palette.components.tooltip.fill,
        },
        tooltipPlacementTop: {
          '.MuiTooltip-arrow': {
            marginBottom: -(tooltipArrow.height - 2) + 'px !important',
          }
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.sm,
          border: '1px solid',
          ...(typography.alert.title),
          zIndex: zIndex.alert,
        },
        standardSuccess: {
          borderColor: palette.success.main,
          color: palette.success.main,
        },
        standardError: {
          borderColor: palette.error.main,
          color: palette.error.main,
        },
        standardWarning: {
          color: palette.warning.main,
          backgroundColor: palette.components.alert.warning.background,
          borderColor: palette.components.alert.warning.border,
        },
        standardInfo: {
          borderColor: palette.info.main,
          color: palette.components.alert.info.color,
        },
        filledInfo: {
          '& .MuiAlertTitle-root, & .MuiTypography-root': {
            color: palette.info.contrastText,
          },
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          ...typography.alert.title,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: palette.primary.main,
          ':hover': {
            color: palette.primary.main,
          },
        },
        underlineNone: {
          ':visited': {
            color: palette.text.visited,
          },
          '&.Mui-focusVisible': {
            borderRadius: '4px',
            boxShadow: `0px 0px 0px 2px ${palette.text.outlineBorder}`,
            display: 'inline-flex',
            outline: 'none',
          }
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          ...getFontProps('Borna, sans-serif', 500, '1.125rem'),
          lineHeight: '1.75rem',
          letterSpacing: '0.15px',
          color: palette.text.primary,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          ...getFontProps('Poppins, sans-serif', 500, '1rem'),
        },
      },
    },
    MuiTypography: {
      variants: [
        {
          props: { variant: 'menu.itemDense' },
          style: {
            ...typography.menu.itemDense,
          },
        },
      ],
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          boxShadow: boxShadows.elevation8,
          borderRadius: borderRadius.xs,
        },
        listbox: {
          padding: spaces.xs,
        },
        option: {
          padding: `6px ${spaces.md}`,
          borderRadius: borderRadius.sm,
          ...getFontProps('Poppins, sans-serif', 400, '1rem'),
          lineHeight: '1.75rem',
          letterSpacing: '0.15px',
          color: palette.text.primary,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'list-item!important',
        },
      },
    },
  }
});

Object.keys(boxShadows).forEach(key => {
  const elevation = parseInt(key.replace('elevation', ''), 10);
  if (!isNaN(elevation)) {
    mainTheme.shadows[elevation] = boxShadows[key as keyof typeof boxShadows];
  }
});

export default mainTheme;
